import React from 'react';
import {
  Alert, Badge, Button, ErrorState, FormField, InnerPageHeader, NumberInput, Select,
  SkeletonCard, Spinner, TextInput, Textarea, useToast,
} from '@parsina/framework';
import { Play, Save, Zap } from 'lucide-react';

import { useTranslation, LTR_TEXT } from '../../i18n';
import { errorMessage, gql } from '../../lib/api';
import { useShortcut, Kbd } from '../../lib/shortcuts';
import {
  CREDIT_IRT, EMPTY, creditPriceDiffers, formatCreditPrice, isIrtCredit,
} from '../../lib/format';
import {
  COST_BASES, POLICY_WINDOWS, ROUNDINGS, blankPolicyInput, num, policyToInput, sameInput,
  serialisePolicyInput, validatePolicyInput,
} from '../../lib/aiPricingView';
import {
  ACTIVATE_POLICY, CREDIT_PRICING_QUERY, POLICY_QUERY, SAVE_POLICY_DRAFT, SET_SERVICE_OVERRIDE,
  SIMULATE_POLICY,
} from './queries';
import PolicyResultsTable from './PolicyResultsTable';
import ActivateDialog from './ActivateDialog';
import { CsvExportButton, useEnumLabel } from './shared';

const STATUS_VARIANT = { draft: 'outline', active: 'success', archived: 'outline' };

/** A stable key for "the parameters as they are now", for simulate-gating. */
const inputKey = (input) => JSON.stringify(serialisePolicyInput(input));

/**
 * The policy editor — Archetype C (record), at `?policy=<id>` or `?policy=new`
 * (design §12.6, flows F6–F9).
 *
 * THE GATE IS THE STATE MACHINE. «فعال‌سازی…» is enabled only when the draft is
 * SAVED, UNCHANGED since the save, and SIMULATED with exactly these parameters.
 * Each of those is a real condition: activation reads the persisted rows, so a
 * draft with unsaved parameters would activate the old ones; and a simulation
 * is the only moment the operator sees the prices they are about to charge.
 *
 * Every write follows §8.1: the failure stays on screen with the input intact,
 * and a `BUSINESS_RULE` on activation (another operator won the race) keeps the
 * dialog open and offers a reload rather than discarding anything.
 *
 * THE CREDIT'S PRICE HAS A CURRENCY (PM-0211). IRT — every Parsina policy — is
 * the screen as it always was: a USD rate to enter and the Parseh value in
 * toman. Any other currency (USD on Proxia) prices a credit with no exchange
 * rate at all, so there is no rate field, and the credit's price is shown in
 * its own currency. A saved policy says which it was priced in; a new draft
 * takes the wallet's.
 */
export default function PolicyEditor({ policyId, cloneFromId, policies, onExit, onDirtyChange, onActivated, onSaved }) {
  const {
    t, locale, formatNumber, formatPercent, formatDigits, formatMoney,
  } = useTranslation();
  const toast = useToast();
  const basisLabel = useEnumLabel('aiPricing.policies.editor.basisOptions');
  const statusLabel = useEnumLabel('aiPricing.policies.status');

  const isNew = policyId === 'new';
  const [detail, setDetail] = React.useState(null);
  const [form, setForm] = React.useState(blankPolicyInput());
  const [savedInput, setSavedInput] = React.useState(null);
  const [simulatedFor, setSimulatedFor] = React.useState(null);
  const [walletParseh, setWalletParseh] = React.useState(null);
  const [walletCredit, setWalletCredit] = React.useState(null);   // { amountMinor, currency }
  const [loading, setLoading] = React.useState(true);
  const [loadError, setLoadError] = React.useState(null);
  const [pending, setPending] = React.useState(null);      // save | simulate | activate | override:<key>
  const [error, setError] = React.useState(null);
  const [activating, setActivating] = React.useState(false);
  const [activateError, setActivateError] = React.useState(null);
  const [conflict, setConflict] = React.useState(false);
  const [touched, setTouched] = React.useState(false);

  // Read through a ref: the list is only a pre-fill for a blank draft, and a
  // `load` that depended on it re-fetched — discarding the operator's edits —
  // the moment the policies list finished loading beside the editor.
  const policiesRef = React.useRef(policies);
  policiesRef.current = policies;

  const load = React.useCallback(() => {
    setLoading(true);
    setLoadError(null);
    const source = isNew ? cloneFromId : policyId;
    const detailPromise = source ? gql(POLICY_QUERY, { id: source }).then((d) => d?.aiPricingPolicy || null) : Promise.resolve(null);
    const walletPromise = gql(CREDIT_PRICING_QUERY).then((d) => d?.creditPricing ?? null).catch(() => null);
    Promise.all([detailPromise, walletPromise])
      .then(([loaded, wallet]) => {
        setWalletParseh(wallet?.parsehToman ?? null);
        setWalletCredit(wallet?.creditPrice ?? null);
        if (isNew) {
          // A blank draft pre-fills the FX from the most recent policy; a
          // clone pre-fills everything and keeps the name for the operator to edit.
          const last = (policiesRef.current || [])[0];
          setForm(loaded ? policyToInput(loaded.policy) : blankPolicyInput({ fxTomanPerUsd: last?.fxTomanPerUsd }));
          setDetail(loaded ? { ...loaded, policy: { ...loaded.policy, id: null, status: 'draft', version: null } } : null);
          setSavedInput(null);
          setSimulatedFor(null);
        } else {
          setDetail(loaded);
          const input = policyToInput(loaded?.policy);
          setForm(input);
          setSavedInput(input);
          // A draft the server has already computed can be activated as it stands.
          setSimulatedFor(loaded?.policy?.computedAt ? inputKey(input) : null);
        }
        setTouched(false);
      })
      .catch((e) => setLoadError(errorMessage(e, t)))
      .finally(() => setLoading(false));
  }, [policyId, cloneFromId, isNew, t]);

  React.useEffect(() => { load(); }, [load]);

  const policy = detail?.policy;
  const status = isNew ? 'draft' : (policy?.status || 'draft');
  const editable = status === 'draft';
  const dirty = editable && (savedInput ? !sameInput(form, savedInput) : touched);
  // What this policy prices a credit in: its own record, or for a new draft the
  // wallet's — today's IRT when neither says.
  const creditCurrency = (isNew && walletCredit?.currency) || policy?.creditCurrency
    || walletCredit?.currency || CREDIT_IRT;
  const withFx = isIrtCredit(creditCurrency);
  const errors = validatePolicyInput(form, { withFx, locale });
  const valid = Object.keys(errors).length === 0;
  const simulated = simulatedFor === inputKey(form);
  const canSave = editable && valid && !pending && (dirty || isNew);
  const canSimulate = editable && valid && !pending;
  const canActivate = editable && !isNew && !!policy?.id && !dirty && simulated && !pending;
  const canOverride = editable && !isNew && !!policy?.id && !pending;

  React.useEffect(() => { onDirtyChange?.(dirty); }, [dirty, onDirtyChange]);
  React.useEffect(() => () => onDirtyChange?.(false), [onDirtyChange]);

  // Reload / close with unsaved parameters asks first (§10 dirty guard).
  React.useEffect(() => {
    if (!dirty) return undefined;
    const handler = (e) => { e.preventDefault(); e.returnValue = ''; };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [dirty]);

  const set = (key) => (e) => { setTouched(true); setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e })); };

  const runWrite = async (kind, mutation, variables, onOk) => {
    setPending(kind);
    setError(null);
    try {
      const d = await gql(mutation, variables);
      onOk(d);
    } catch (e) {
      setError(errorMessage(e, t));
      throw e;
    } finally {
      setPending(null);
    }
  };

  const save = () => {
    if (!canSave) return;
    return runWrite('save', SAVE_POLICY_DRAFT, {
      id: isNew ? null : policy?.id,
      cloneFromId: isNew ? (cloneFromId || null) : null,
      input: serialisePolicyInput(form, { withFx }),
      expectedVersion: isNew ? null : policy?.version ?? null,
    }, (d) => {
      const next = d?.saveAiPricingPolicyDraft;
      setDetail(next);
      const input = policyToInput(next?.policy);
      setForm(input);
      setSavedInput(input);
      setSimulatedFor(null);
      setTouched(false);
      toast.success(t('aiPricing.policies.editor.saved'));
      onSaved?.(next?.policy?.id);
    }).catch(() => {});
  };

  const simulate = () => {
    if (!canSimulate) return;
    // A saved, unchanged draft recomputes its persisted rows; anything else is a
    // dry run of the parameters on screen, which persists nothing (§8.4).
    const persisted = !isNew && policy?.id && !dirty;
    return runWrite('simulate', SIMULATE_POLICY, persisted
      ? { id: policy.id }
      : { id: null, input: serialisePolicyInput(form, { withFx }) }, (d) => {
      const next = d?.simulateAiPricingPolicy;
      setDetail((prev) => (persisted || !prev ? next : { ...next, policy: prev.policy }));
      setSimulatedFor(inputKey(form));
      toast.success(t('aiPricing.policies.editor.simulated'));
    }).catch(() => {});
  };

  const override = (serviceKey, parseh) => {
    if (!canOverride) return;
    return runWrite(`override:${serviceKey}`, SET_SERVICE_OVERRIDE, {
      policyId: policy.id, serviceKey, parseh, expectedVersion: policy.version,
    }, (d) => {
      setDetail(d?.setAiServiceOverride);
      toast.success(t('aiPricing.policies.editor.overrideSaved'));
    }).catch(() => {});
  };

  const activate = async () => {
    setPending('activate');
    setActivateError(null);
    setConflict(false);
    try {
      await gql(ACTIVATE_POLICY, { id: policy.id, expectedVersion: policy.version });
      setActivating(false);
      toast.success(t('aiPricing.policies.editor.activated'));
      onActivated?.();
    } catch (e) {
      setActivateError(errorMessage(e, t));
      setConflict(e?.code === 'BUSINESS_RULE');
    } finally {
      setPending(null);
    }
  };

  const reloadAfterConflict = () => {
    setActivateError(null);
    setConflict(false);
    setActivating(false);
    setSimulatedFor(null);
    load();
  };

  useShortcut('ctrl+s', save, { label: t('shortcuts.action.saveDraft'), group: t('shortcuts.groups.aiPricing'), enabled: canSave });
  useShortcut('ctrl+enter', simulate, { label: t('shortcuts.action.simulate'), group: t('shortcuts.groups.aiPricing'), enabled: canSimulate && !activating });

  const title = isNew ? t('aiPricing.policies.editor.newTitle') : (policy?.name || t('common.value.none'));
  // Toman against toman only: a USD policy has no toman snapshot, while the
  // wallet still reports its COIN_PRICE_TOMAN.
  const walletChanged = withFx && walletParseh != null && policy?.parsehToman != null && num(walletParseh) !== num(policy.parsehToman);
  // The same warning for a credit priced in another currency: the wallet's live
  // price against the one this policy was computed on.
  const priceArgs = { t, formatNumber, formatMoney };
  const creditChange = !withFx && policy && creditPriceDiffers(
    { price: policy.creditPrice, currency: policy.creditCurrency }, walletCredit,
  )
    ? t('aiPricing.policies.editor.creditChanged', {
      wallet: formatCreditPrice(walletCredit.amountMinor, walletCredit.currency, { ...priceArgs, minor: true }),
      snapshot: formatCreditPrice(policy.creditPrice, policy.creditCurrency, priceArgs),
    })
    : null;

  const csvColumns = [
    { key: 'serviceKey', header: t('aiPricing.policies.editor.results.columns.service'), value: (p) => p.serviceKey },
    { key: 'basisCostUsd', header: t('aiPricing.policies.editor.results.columns.basis'), value: (p) => p.basisCostUsd },
    { key: 'basisSource', header: t('aiPricing.services.drawer.basis'), value: (p) => p.basisSource },
    { key: 'costParseh', header: t('aiPricing.policies.editor.results.columns.costParseh'), value: (p) => p.costParseh },
    { key: 'computedParseh', header: t('aiPricing.policies.editor.results.columns.computed'), value: (p) => p.computedParseh },
    { key: 'overrideParseh', header: t('aiPricing.policies.editor.results.columns.override'), value: (p) => p.overrideParseh },
    { key: 'effectiveParseh', header: t('aiPricing.policies.editor.results.columns.effective'), value: (p) => p.effectiveParseh },
    { key: 'marginPct', header: t('aiPricing.policies.editor.results.columns.margin'), value: (p) => p.marginPct },
    { key: 'activeParseh', header: t('aiPricing.policies.editor.results.columns.active'), value: (p) => p.activeParseh },
    { key: 'deltaParseh', header: t('aiPricing.policies.editor.results.columns.delta'), value: (p) => p.deltaParseh },
  ];

  const numberField = (key, labelKey, { unit, min, max, step } = {}) => (
    <NumberInput
      label={t(`aiPricing.policies.editor.${labelKey}`)}
      value={form[key] ?? ''}
      onChange={set(key)}
      unit={unit}
      min={min}
      max={max}
      validate={(v) => {
        if (!v) return '';
        const n = Number(v);
        if (!Number.isFinite(n)) return t('validation.required');
        if (min != null && n < min) return t('validation.required');
        if (max != null && n > max) return t('validation.required');
        return '';
      }}
      error={errors[key] ? t(errors[key]) : undefined}
      disabled={!editable}
      data-testid={`ai-policy-${key}`}
      inputMode="decimal"
      step={step}
    />
  );

  return (
    <div className="flex h-full min-h-0 flex-col" data-testid="ai-policy-editor" data-state={loading ? 'loading' : loadError ? 'error' : 'content'}>
      <InnerPageHeader
        title={title}
        onBack={onExit}
        backLabel={t('aiPricing.policies.editor.back')}
        status={<Badge variant={STATUS_VARIANT[status] || 'outline'}>{statusLabel(status)}</Badge>}
        badges={policy?.version != null ? <Badge variant="outline"><span {...LTR_TEXT}>v{policy.version}</span></Badge> : undefined}
        data-testid="ai-policy-header"
      />

      <div className="min-h-0 flex-1 overflow-y-auto px-8 py-6">
        <div className="mx-auto w-full max-w-[1440px]">
          {loading ? (
            <div className="grid grid-cols-1 gap-5 xl:grid-cols-[22rem_minmax(0,1fr)]" data-testid="ai-policy-loading" data-state="loading" role="status" aria-busy="true" aria-label={t('aiPricing.policies.editor.loading')}>
              <SkeletonCard />
              <SkeletonCard />
            </div>
          ) : loadError ? (
            <ErrorState
              title={t('aiPricing.policies.editor.error')}
              description={loadError}
              onRetry={load}
              retryLabel={t('common.actions.retry')}
              data-testid="ai-policy-error"
            />
          ) : (
            <>
              <div className="mb-5 flex flex-col gap-3">
                {!editable ? (
                  <Alert variant="info" data-testid="ai-policy-readonly">
                    {t('aiPricing.policies.editor.readOnly', { status: statusLabel(status) })}
                  </Alert>
                ) : null}
                {error ? <Alert variant="error" data-testid="ai-policy-write-error">{error}</Alert> : null}
                {editable && !simulated && !isNew && savedInput ? (
                  <Alert variant="warning" data-testid="ai-policy-unsimulated">{t('aiPricing.policies.editor.unsimulated')}</Alert>
                ) : null}
                {walletChanged ? (
                  <Alert variant="warning" data-testid="ai-policy-wallet-changed">
                    {t('aiPricing.policies.editor.parsehChanged', {
                      wallet: formatNumber(num(walletParseh)),
                      snapshot: formatNumber(num(policy.parsehToman)),
                    })}
                  </Alert>
                ) : creditChange ? (
                  <Alert variant="warning" data-testid="ai-policy-wallet-changed">{creditChange}</Alert>
                ) : null}
              </div>

              <div className="grid grid-cols-1 gap-5 xl:grid-cols-[22rem_minmax(0,1fr)]">
                {/* Parameters */}
                <form
                  className="flex min-w-0 flex-col gap-3 rounded-lg border border-border bg-card p-5"
                  onSubmit={(e) => { e.preventDefault(); simulate(); }}
                  aria-label={t('aiPricing.policies.editor.params')}
                  data-testid="ai-policy-params"
                >
                  <h2 className="text-sm font-bold text-foreground">{t('aiPricing.policies.editor.params')}</h2>
                  <TextInput
                    label={t('aiPricing.policies.editor.name')}
                    required
                    value={form.name}
                    onChange={set('name')}
                    error={touched && errors.name ? t(errors.name) : undefined}
                    disabled={!editable}
                    data-testid="ai-policy-name"
                  />
                  {withFx ? numberField('fxTomanPerUsd', 'fx', { unit: t('aiPricing.policies.editor.fxUnit'), min: 1 }) : null}
                  <FormField label={t('aiPricing.policies.editor.parseh')}>
                    <div className="rounded-lg border border-border bg-muted px-3 py-2 text-sm tabular-nums" data-testid="ai-policy-parseh">
                      {withFx ? (
                        policy?.parsehToman != null
                          ? t('aiPricing.policies.editor.parsehSnapshot', { value: formatNumber(num(policy.parsehToman)) })
                          : walletParseh != null
                            ? t('aiPricing.policies.editor.parsehFrom', { value: formatNumber(num(walletParseh)) })
                            : EMPTY
                      ) : policy?.creditPrice != null && !isNew
                        ? t('aiPricing.policies.editor.creditSnapshot', {
                          value: formatCreditPrice(policy.creditPrice, policy.creditCurrency, priceArgs),
                        })
                        : walletCredit
                          ? t('aiPricing.policies.editor.creditFrom', {
                            value: formatCreditPrice(walletCredit.amountMinor, walletCredit.currency, { ...priceArgs, minor: true }),
                          })
                          : EMPTY}
                    </div>
                  </FormField>
                  {numberField('targetMarginPct', 'margin', { unit: t('aiPricing.policies.editor.percent'), min: 0, max: 99 })}
                  {numberField('overheadPct', 'overhead', { unit: t('aiPricing.policies.editor.percent'), min: 0 })}
                  <Select
                    label={t('aiPricing.policies.editor.basis')}
                    value={form.costBasis}
                    onChange={set('costBasis')}
                    options={COST_BASES.map((b) => ({ value: b, label: basisLabel(b) }))}
                    placeholder={t('aiPricing.policies.editor.basis')}
                    disabled={!editable}
                    data-testid="ai-policy-basis"
                  />
                  <Select
                    label={t('aiPricing.policies.editor.window')}
                    value={String(form.windowDays)}
                    onChange={(e) => { setTouched(true); setForm((f) => ({ ...f, windowDays: Number(e.target?.value ?? e) })); }}
                    options={POLICY_WINDOWS.map((d) => ({ value: String(d), label: t(`aiPricing.policies.editor.windowOptions.${d}`) }))}
                    placeholder={t('aiPricing.policies.editor.window')}
                    disabled={!editable}
                    data-testid="ai-policy-window"
                  />
                  <Select
                    label={t('aiPricing.policies.editor.rounding')}
                    value={form.rounding}
                    onChange={set('rounding')}
                    options={ROUNDINGS.map((r) => ({ value: r, label: t(`aiPricing.policies.editor.roundingOptions.${r}`) }))}
                    placeholder={t('aiPricing.policies.editor.rounding')}
                    disabled={!editable}
                    data-testid="ai-policy-rounding"
                  />
                  {numberField('minParseh', 'minParseh', { unit: t('common.units.parseh'), min: 0 })}
                  <Textarea
                    label={t('aiPricing.policies.editor.note')}
                    value={form.note}
                    onChange={set('note')}
                    rows={2}
                    maxLength={500}
                    disabled={!editable}
                    data-testid="ai-policy-note"
                  />
                  {editable ? (
                    <Button type="submit" variant="outline" disabled={!canSimulate} aria-keyshortcuts="Control+Enter" data-testid="ai-policy-simulate">
                      {pending === 'simulate' ? <Spinner size={16} thickness={2} color="currentColor" /> : <Play size={16} aria-hidden />}
                      {t('aiPricing.policies.editor.simulate')}
                      <Kbd combo="ctrl+enter" style={{ background: 'transparent', border: '1px solid currentColor', opacity: 0.6 }} />
                    </Button>
                  ) : null}
                </form>

                {/* Results */}
                <section className="min-w-0 rounded-lg border border-border bg-card" aria-label={t('aiPricing.policies.editor.results.title')}>
                  <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
                    <h2 className="text-sm font-bold text-foreground">{t('aiPricing.policies.editor.results.title')}</h2>
                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-muted-foreground" data-testid="ai-policy-results-summary">
                      {detail ? (
                        <>
                          <span data-testid="ai-policy-projected">{t('aiPricing.policies.editor.results.projected', { pct: num(detail.projectedBlendedMarginPct) == null ? EMPTY : formatPercent(num(detail.projectedBlendedMarginPct)) })}</span>
                          <span>{t('aiPricing.policies.editor.results.counts', { dearer: detail.dearer || 0, cheaper: detail.cheaper || 0, negative: detail.negative || 0 })}</span>
                        </>
                      ) : null}
                      <CsvExportButton columns={csvColumns} rows={detail?.prices || []} data-testid="ai-policy-csv" />
                    </div>
                  </div>
                  <PolicyResultsTable
                    prices={detail?.prices || []}
                    canOverride={canOverride}
                    overrideDisabledReason={isNew ? t('aiPricing.policies.editor.saveFirst') : t('aiPricing.services.drawer.editActiveHint')}
                    onOverride={override}
                    pendingKey={pending?.startsWith('override:') ? pending.slice('override:'.length) : null}
                  />
                </section>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Sticky commit row. Composed from kit Buttons rather than `SaveBar`,
          which holds exactly two actions; this record has four (§12.6). */}
      {!loading && !loadError ? (
        <div
          className="sticky bottom-0 flex flex-wrap items-center gap-2 border-t border-border bg-card px-8 py-3"
          data-testid="ai-policy-savebar"
        >
          {editable ? (
            <>
              <Button onClick={save} disabled={!canSave} aria-keyshortcuts="Control+S" data-testid="ai-policy-save">
                {pending === 'save' ? <Spinner size={16} thickness={2} color="currentColor" /> : <Save size={16} aria-hidden />}
                {t('aiPricing.policies.editor.saveDraft')}
                <Kbd combo="ctrl+s" style={{ background: 'transparent', border: '1px solid currentColor', opacity: 0.6 }} />
              </Button>
              <Button
                variant="secondary"
                onClick={() => { setActivateError(null); setConflict(false); setActivating(true); }}
                disabled={!canActivate}
                title={canActivate ? undefined : t('aiPricing.policies.editor.activateHint')}
                data-testid="ai-policy-activate"
              >
                <Zap size={16} aria-hidden />
                {t('aiPricing.policies.editor.activate')}
              </Button>
            </>
          ) : null}
          <Button variant="outline" onClick={onExit} data-testid="ai-policy-cancel">
            {t('aiPricing.policies.editor.cancel')}
          </Button>
          {policy?.id ? (
            <span className="ms-auto text-xs text-muted-foreground">
              {t('aiPricing.services.source.policy', { id: formatDigits(policy.id) })}
            </span>
          ) : null}
        </div>
      ) : null}

      <ActivateDialog
        open={activating}
        onOpenChange={(open) => { if (!open && pending !== 'activate') setActivating(false); }}
        detail={detail}
        pending={pending === 'activate'}
        error={activateError}
        conflict={conflict}
        onConfirm={activate}
        onReload={reloadAfterConflict}
        creditChange={creditChange}
      />
    </div>
  );
}
